import React, { useEffect, useState } from "react";
import { 
  getIntegrationStatus, 
  testConnection, 
  configureIntegration, 
  syncFromExternal,
  autoMapRecords,
  getAvailableProviders
} from "../utils/api/integrations";

const IntegrationTab = () => {
  const [selectedProvider, setSelectedProvider] = useState(null);
  const [availableProviders, setAvailableProviders] = useState([]);

  const [providerConfigs, setProviderConfigs] = useState({});
  const [currentConfig, setCurrentConfig] = useState({
    enabled: false,
    baseURL: '',
    company: '',
    username: '',
    password: '',
    syncFrequency: 'manual',
    syncOptions: {
      invoices: true,
      payments: true,
      customers: false,
      products: false,
      vendors: false,
      bank_transactions: false
    },
    quickbooks: {
      clientId: '',
      clientSecret: '',
      realmId: '',
      accessToken: '',
      refreshToken: '',
      environment: 'sandbox'
    },
    zoho: {
      clientId: '',
      clientSecret: '',
      organizationId: '',
      accessToken: '',
      refreshToken: '',
      apiDomain: 'https://www.zohoapis.com'
    },
    sap: {
      server: '',
      companyDB: '',
      username: '',
      password: '',
      language: 25,
      licenseServer: ''
    }
  });
  
  const [syncStatus, setSyncStatus] = useState({
    lastSync: null,
    status: 'disconnected',
    pendingMappings: 0,
    testResult: null,
    syncSummary: {
      invoices: 0,
      payments: 0,
      customers: 0,
      products: 0
    }
  });
  
  const [showConfigModal, setShowConfigModal] = useState(false);
  const [showProviderSelectModal, setShowProviderSelectModal] = useState(false);
  const [isTesting, setIsTesting] = useState(false);
  const [isSyncing, setIsSyncing] = useState(false);
  const [syncResult, setSyncResult] = useState(null);

  useEffect(() => {
    loadIntegrationStatus();
    loadAvailableProviders();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedProvider]);

  const loadIntegrationStatus = async () => {
    try {
      const status = await getIntegrationStatus(selectedProvider);
      setSyncStatus({
        lastSync: status.lastSync,
        status: status.enabled ? 'connected' : 'disconnected',
        pendingMappings: status.pendingMappings || 0,
        testResult: null,
        syncSummary: status.syncSummary || {
          invoices: 0,
          payments: 0,
          customers: 0,
          products: 0
        }
      });
      
      if (status.config) {
        const mergedConfig = {
          ...currentConfig,
          ...status.config,
          enabled: status.enabled,
          syncOptions: {
            ...currentConfig.syncOptions,
            ...(status.config.syncOptions || {})
          }
        };
        
        setCurrentConfig(mergedConfig);
        setProviderConfigs(prev => ({
          ...prev,
          [selectedProvider]: mergedConfig
        }));
      }
    } catch (error) {
      console.error("Error loading integration status:", error);
    }
  };
  const loadAvailableProviders = async () => {
    try {
      await getAvailableProviders();
  

      setAvailableProviders([
        { id: 'tally', name: 'Tally ERP', icon: '📊', description: 'Indian accounting software' },
        { id: 'quickbooks', name: 'QuickBooks', icon: '💼', description: 'International accounting' },
        { id: 'zoho', name: 'Zoho Books', icon: '📈', description: 'Cloud accounting suite' },
        { id: 'sap', name: 'SAP Business One', icon: '🏢', description: 'Enterprise ERP system' },
        { id: 'xero', name: 'Xero', icon: '🌐', description: 'Cloud-based accounting' },
        { id: 'freshbooks', name: 'FreshBooks', icon: '📖', description: 'Invoicing & accounting' },
        { id: 'sage', name: 'Sage Intacct', icon: '🧾', description: 'Advanced financial management' },
        { id: 'netsuite', name: 'Oracle NetSuite', icon: '☁️', description: 'Cloud ERP solution' }
      ]);
    } catch (error) {
      console.error("Error loading providers:", error);
    }
  };
  

  const handleSelectProviderClick = () => {
    setShowProviderSelectModal(true);
  };

  const handleSelectProvider = async (providerId) => {
    setSelectedProvider(providerId);
    setShowProviderSelectModal(false);
    
    if (providerConfigs[providerId]) {
      setCurrentConfig(providerConfigs[providerId]);
    } else {
      setCurrentConfig({
        enabled: false,
        baseURL: getDefaultBaseURL(providerId),
        company: '',
        username: '',
        password: '',
        syncFrequency: 'manual',
        syncOptions: {
          invoices: true,
          payments: true,
          customers: false,
          products: false,
          vendors: false,
          bank_transactions: false
        },
        ...getProviderDefaults(providerId)

      });
    }
    await loadIntegrationStatus()

  };

  const getDefaultBaseURL = (provider) => {
    const defaults = {
      tally: 'http://localhost:9000',
      quickbooks: 'https://quickbooks.api.intuit.com',
      zoho: 'https://www.zohoapis.com',
      sap: 'http://localhost:50000',
      xero: 'https://api.xero.com',
      freshbooks: 'https://api.freshbooks.com',
      sage: 'https://api.intacct.com',
      netsuite: 'https://{account-id}.suitetalk.api.netsuite.com'
    };
    return defaults[provider] || '';
  };
  // Get provider-specific default config
  const getProviderDefaults = (provider) => {
    switch (provider) {
      case 'quickbooks':
        return {
          quickbooks: {
            clientId: '',
            clientSecret: '',
            realmId: '',
            environment: 'sandbox'
          }
        }
      case 'zoho':
        return {
          zoho: {
            clientId: '',
            clientSecret: '',
            organizationId: '',
            apiDomain: 'https://www.zohoapis.com'
          }
        }
      case 'sap':
        return {
          sap: {
            server: '',
            companyDB: '',
            username: '',
            password: '',
            language: 25
          }
        }
      default:
        return {}
    }
  }
  const handleConfigureClick = () => {
    setShowConfigModal(true);
  };

  const handleSyncOptionChange = (option, value) => {
    setCurrentConfig(prev => ({
      ...prev,
      syncOptions: {
        ...prev.syncOptions,
        [option]: value
      }
    }));
  };

  const handleTestConnection = async () => {
    setIsTesting(true);
    try {
      const credentials = getCredentialsForProvider(selectedProvider, currentConfig)

      const result = await testConnection(selectedProvider, credentials);
      setSyncStatus(prev => ({
        ...prev,
        testResult: result
      }));
      
      if (result.success) {
        alert(`Connection test successful! ${result.message}`);
      } else {
        alert(`Connection failed: ${result.message}`);
      }
    } catch (error) {
      alert(`Test failed: ${error.message}`);
    } finally {
      setIsTesting(false);
    }
  };
  // Get credentials based on provider type
  const getCredentialsForProvider = (provider, config) => {
    switch (provider) {
      case 'tally':
        return {
          baseURL: config.baseURL,
          username: config.username,
          password: config.password,
          company: config.company
        }
      case 'quickbooks':
        return {
          clientId: config.quickbooks?.clientId,
          clientSecret: config.quickbooks?.clientSecret,
          realmId: config.quickbooks?.realmId,
          accessToken: config.quickbooks?.accessToken,
          refreshToken: config.quickbooks?.refreshToken,
          environment: config.quickbooks?.environment
        }
      case 'zoho':
        return {
          clientId: config.zoho?.clientId,
          clientSecret: config.zoho?.clientSecret,
          organizationId: config.zoho?.organizationId,
          accessToken: config.zoho?.accessToken,
          refreshToken: config.zoho?.refreshToken,
          apiDomain: config.zoho?.apiDomain
        }
      case 'sap':
        return {
          server: config.sap?.server,
          companyDB: config.sap?.companyDB,
          username: config.sap?.username,
          password: config.sap?.password,
          language: config.sap?.language,
          licenseServer: config.sap?.licenseServer
        }
      default:
        return config
    }
  }
  const handleSaveConfig = async () => {
    try {
      const configToSave = {
        ...currentConfig,
        enabled: true,
        provider: selectedProvider
      };
      
      await configureIntegration(selectedProvider, configToSave);
      
      setSyncStatus(prev => ({
        ...prev,
        status: 'connected'
      }));
      
      setProviderConfigs(prev => ({
        ...prev,
        [selectedProvider]: configToSave
      }));
      
      setShowConfigModal(false);
      alert('Configuration saved successfully!');
      loadIntegrationStatus();
    } catch (error) {
      alert('Save failed: ' + error.message);
    }
  };

  const handleSync = async (type = 'full') => {
    if (syncStatus.status !== 'connected') {
      alert(`Please configure and connect ${getProviderName(selectedProvider)} first`);
      return;
    }
    
    setIsSyncing(true);
    setSyncResult(null);
    
    try {
      const options = {
        type: type,
        syncOptions: currentConfig.syncOptions,
        provider: selectedProvider
      };
      
      const result = await syncFromExternal(selectedProvider, options);
      setSyncResult(result);
      loadIntegrationStatus();
      
      alert(`
Sync completed:
- Created: ${result.created}
- Updated: ${result.updated}
- Skipped: ${result.skipped}
${result.errors.length > 0 ? `- Errors: ${result.errors.length}` : ''}
      `.trim());
      
    } catch (error) {
      alert('Sync failed: ' + error.message);
    } finally {
      setIsSyncing(false);
    }
  };

  // eslint-disable-next-line no-unused-vars
  const handleAutoMap = async () => {
    if (syncStatus.pendingMappings === 0) {
      alert('No pending mappings');
      return;
    }
    
    try {
      const result = await autoMapRecords(selectedProvider, {
        matchingStrategy: 'auto',
        confidenceThreshold: 0.8
      });
      
      alert(`Auto-mapping completed: ${result.length} records mapped`);
      loadIntegrationStatus();
    } catch (error) {
      alert('Auto-mapping failed: ' + error.message);
    }
  };

  const getProviderName = (providerId) => {
    const provider = availableProviders?.find(p => p.id === providerId);
    return provider?.name || providerId;
  };

  const getProviderIcon = (providerId) => {
    const provider = availableProviders?.find(p => p.id === providerId);
    return provider?.icon || '📊';
  };
  
  // Selective sync based on options
  const handleSelectiveSync = async () => {
    if (!Object.values(currentConfig.syncOptions).some(v => v)) {
      alert('Please select at least one sync option')
      return
    }
    
    setIsSyncing(true)
    setSyncResult(null)
    
    try {
      const options = {
        type: 'selective',
        syncOptions: currentConfig.syncOptions,
        provider: selectedProvider
      }
      
      const result = await syncFromExternal(selectedProvider, options)
      setSyncResult(result)
      
      // Reload invoices to show synced data
      // loadInvoices()
      loadIntegrationStatus()
      
      // Show sync results
      const message = `
Selective sync completed:
- Created: ${result.created}
- Updated: ${result.updated}
- Skipped: ${result.skipped}
${result.errors.length > 0 ? `- Errors: ${result.errors.length}` : ''}
      `.trim()
      
      alert(message)
      
    } catch (error) {
      alert('Selective sync failed: ' + error.message)
    } finally {
      setIsSyncing(false)
    }
  }
    
  const renderSyncOptions = () => {
    return (
      <div style={{
        padding: 16,
        background: '#f8f9fa',
        borderRadius: 4,
        border: '1px solid #dee2e6',
      }}>
        <h4 style={{ margin: '0 0 12px 0', fontSize: 16 }}>Sync Options</h4>
        <div style={{ 
          display: 'grid', 
          gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))', 
          gap: 12 
        }}>
          <label style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <input 
              type="checkbox" 
              checked={currentConfig.syncOptions?.invoices || true}
              onChange={(e) => handleSyncOptionChange('invoices', e.target.checked)}
            />
            <div>
              <div style={{ fontSize: 14, fontWeight: '500' }}>Invoices</div>
              <div style={{ fontSize: 12, color: '#666' }}>Sales & purchase invoices</div>
            </div>
          </label>
          
          <label style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <input 
              type="checkbox" 
              checked={currentConfig.syncOptions?.payments || true}
              onChange={(e) => handleSyncOptionChange('payments', e.target.checked)}
            />
            <div>
              <div style={{ fontSize: 14, fontWeight: '500' }}>Payments</div>
              <div style={{ fontSize: 12, color: '#666' }}>Receipts & payments</div>
            </div>
          </label>
          
          <label style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <input 
              type="checkbox" 
              checked={currentConfig.syncOptions?.customers || false}
              onChange={(e) => handleSyncOptionChange('customers', e.target.checked)}
            />
            <div>
              <div style={{ fontSize: 14, fontWeight: '500' }}>Customers</div>
              <div style={{ fontSize: 12, color: '#666' }}>Customer master data</div>
            </div>
          </label>
          
          <label style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <input 
              type="checkbox" 
              checked={currentConfig.syncOptions?.products || false}
              onChange={(e) => handleSyncOptionChange('products', e.target.checked)}
            />
            <div>
              <div style={{ fontSize: 14, fontWeight: '500' }}>Products</div>
              <div style={{ fontSize: 12, color: '#666' }}>Items & services</div>
            </div>
          </label>
          
          <label style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <input 
              type="checkbox" 
              checked={currentConfig.syncOptions?.vendors || false}
              onChange={(e) => handleSyncOptionChange('vendors', e.target.checked)}
            />
            <div>
              <div style={{ fontSize: 14, fontWeight: '500' }}>Vendors</div>
              <div style={{ fontSize: 12, color: '#666' }}>Supplier master data</div>
            </div>
          </label>
          
          <label style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <input 
              type="checkbox" 
              checked={currentConfig.syncOptions?.bank_transactions || false}
              onChange={(e) => handleSyncOptionChange('bank_transactions', e.target.checked)}
            />
            <div>
              <div style={{ fontSize: 14, fontWeight: '500' }}>Bank Transactions</div>
              <div style={{ fontSize: 12, color: '#666' }}>Bank statements</div>
            </div>
          </label>
        </div>
        
        <div style={{ marginTop: 12, fontSize: 12, color: '#666' }}>
          <strong>Note:</strong> Only enabled options will be synced. Disabling options improves sync speed.
        </div>
      </div>
    )
  }
  
  // Render provider-specific configuration fields
  const renderProviderSpecificFields = () => {
    switch (selectedProvider) {
      case 'quickbooks':
        return (
          <>
            <div>
              <label style={{ display: 'block', marginBottom: 4, fontSize: 14, fontWeight: '500' }}>
                Client ID *
              </label>
              <input
                type="text"
                value={currentConfig.quickbooks?.clientId || ''}
                onChange={(e) => setCurrentConfig(prev => ({
                  ...prev,
                  quickbooks: { ...prev.quickbooks, clientId: e.target.value }
                }))}
                placeholder="QuickBooks Client ID"
                style={{
                  width: '100%',
                  padding: '8px 12px',
                  border: '1px solid #ddd',
                  borderRadius: 4,
                  fontSize: 14
                }}
              />
            </div>
            
            <div>
              <label style={{ display: 'block', marginBottom: 4, fontSize: 14, fontWeight: '500' }}>
                Client Secret *
              </label>
              <input
                type="password"
                value={currentConfig.quickbooks?.clientSecret || ''}
                onChange={(e) => setCurrentConfig(prev => ({
                  ...prev,
                  quickbooks: { ...prev.quickbooks, clientSecret: e.target.value }
                }))}
                placeholder="QuickBooks Client Secret"
                style={{
                  width: '100%',
                  padding: '8px 12px',
                  border: '1px solid #ddd',
                  borderRadius: 4,
                  fontSize: 14
                }}
              />
            </div>
            
            <div>
              <label style={{ display: 'block', marginBottom: 4, fontSize: 14, fontWeight: '500' }}>
                Realm ID *
              </label>
              <input
                type="text"
                value={currentConfig.quickbooks?.realmId || ''}
                onChange={(e) => setCurrentConfig(prev => ({
                  ...prev,
                  quickbooks: { ...prev.quickbooks, realmId: e.target.value }
                }))}
                placeholder="Company/Realm ID"
                style={{
                  width: '100%',
                  padding: '8px 12px',
                  border: '1px solid #ddd',
                  borderRadius: 4,
                  fontSize: 14
                }}
              />
            </div>
            
            <div>
              <label style={{ display: 'block', marginBottom: 4, fontSize: 14, fontWeight: '500' }}>
                Environment
              </label>
              <select
                value={currentConfig.quickbooks?.environment || 'sandbox'}
                onChange={(e) => setCurrentConfig(prev => ({
                  ...prev,
                  quickbooks: { ...prev.quickbooks, environment: e.target.value }
                }))}
                style={{
                  width: '100%',
                  padding: '8px 12px',
                  border: '1px solid #ddd',
                  borderRadius: 4,
                  fontSize: 14,
                  background: 'white'
                }}
              >
                <option value="sandbox">Sandbox (Test)</option>
                <option value="production">Production</option>
              </select>
            </div>
            
            <div style={{ padding: 12, background: '#fff3e0', borderRadius: 4, fontSize: 12 }}>
              <strong>QuickBooks Setup:</strong>
              <ul style={{ margin: '8px 0 0 0', paddingLeft: 20 }}>
                <li>Create app on QuickBooks Developer Portal</li>
                <li>Get Client ID and Client Secret</li>
                <li>Realm ID is your company ID</li>
                <li>Use OAuth flow for initial authentication</li>
              </ul>
            </div>
          </>
        )
      
      case 'zoho':
        return (
          <>
            <div>
              <label style={{ display: 'block', marginBottom: 4, fontSize: 14, fontWeight: '500' }}>
                Client ID *
              </label>
              <input
                type="text"
                value={currentConfig.zoho?.clientId || ''}
                onChange={(e) => setCurrentConfig(prev => ({
                  ...prev,
                  zoho: { ...prev.zoho, clientId: e.target.value }
                }))}
                placeholder="Zoho Client ID"
                style={{
                  width: '100%',
                  padding: '8px 12px',
                  border: '1px solid #ddd',
                  borderRadius: 4,
                  fontSize: 14
                }}
              />
            </div>
            
            <div>
              <label style={{ display: 'block', marginBottom: 4, fontSize: 14, fontWeight: '500' }}>
                Client Secret *
              </label>
              <input
                type="password"
                value={currentConfig.zoho?.clientSecret || ''}
                onChange={(e) => setCurrentConfig(prev => ({
                  ...prev,
                  zoho: { ...prev.zoho, clientSecret: e.target.value }
                }))}
                placeholder="Zoho Client Secret"
                style={{
                  width: '100%',
                  padding: '8px 12px',
                  border: '1px solid #ddd',
                  borderRadius: 4,
                  fontSize: 14
                }}
              />
            </div>
            
            <div>
              <label style={{ display: 'block', marginBottom: 4, fontSize: 14, fontWeight: '500' }}>
                Organization ID *
              </label>
              <input
                type="text"
                value={currentConfig.zoho?.organizationId || ''}
                onChange={(e) => setCurrentConfig(prev => ({
                  ...prev,
                  zoho: { ...prev.zoho, organizationId: e.target.value }
                }))}
                placeholder="Zoho Organization ID"
                style={{
                  width: '100%',
                  padding: '8px 12px',
                  border: '1px solid #ddd',
                  borderRadius: 4,
                  fontSize: 14
                }}
              />
            </div>
          </>
        )
      
      case 'sap':
        return (
          <>
            <div>
              <label style={{ display: 'block', marginBottom: 4, fontSize: 14, fontWeight: '500' }}>
                Server URL *
              </label>
              <input
                type="text"
                value={currentConfig.sap?.server || ''}
                onChange={(e) => setCurrentConfig(prev => ({
                  ...prev,
                  sap: { ...prev.sap, server: e.target.value }
                }))}
                placeholder="http://server:50000"
                style={{
                  width: '100%',
                  padding: '8px 12px',
                  border: '1px solid #ddd',
                  borderRadius: 4,
                  fontSize: 14
                }}
              />
            </div>
            
            <div>
              <label style={{ display: 'block', marginBottom: 4, fontSize: 14, fontWeight: '500' }}>
                Company Database *
              </label>
              <input
                type="text"
                value={currentConfig.sap?.companyDB || ''}
                onChange={(e) => setCurrentConfig(prev => ({
                  ...prev,
                  sap: { ...prev.sap, companyDB: e.target.value }
                }))}
                placeholder="SAP Company Database"
                style={{
                  width: '100%',
                  padding: '8px 12px',
                  border: '1px solid #ddd',
                  borderRadius: 4,
                  fontSize: 14
                }}
              />
            </div>
            
            <div>
              <label style={{ display: 'block', marginBottom: 4, fontSize: 14, fontWeight: '500' }}>
                SAP Username *
              </label>
              <input
                type="text"
                value={currentConfig.sap?.username || ''}
                onChange={(e) => setCurrentConfig(prev => ({
                  ...prev,
                  sap: { ...prev.sap, username: e.target.value }
                }))}
                placeholder="SAP Username"
                style={{
                  width: '100%',
                  padding: '8px 12px',
                  border: '1px solid #ddd',
                  borderRadius: 4,
                  fontSize: 14
                }}
              />
            </div>
          </>
        )
      
      default:
        // Tally and other basic providers
        return (
          <>
            <div>
              <label style={{ display: 'block', marginBottom: 4, fontSize: 14, fontWeight: '500' }}>
                Base URL *
              </label>
              <input
                type="text"
                value={currentConfig.baseURL}
                onChange={(e) => setCurrentConfig(prev => ({ ...prev, baseURL: e.target.value }))}
                placeholder={getDefaultBaseURL(selectedProvider)}
                style={{
                  width: '100%',
                  padding: '8px 12px',
                  border: '1px solid #ddd',
                  borderRadius: 4,
                  fontSize: 14
                }}
              />
            </div>
            
            <div>
              <label style={{ display: 'block', marginBottom: 4, fontSize: 14, fontWeight: '500' }}>
                Company Name
              </label>
              <input
                type="text"
                value={currentConfig.company}
                onChange={(e) => setCurrentConfig(prev => ({ ...prev, company: e.target.value }))}
                placeholder="Company name"
                style={{
                  width: '100%',
                  padding: '8px 12px',
                  border: '1px solid #ddd',
                  borderRadius: 4,
                  fontSize: 14
                }}
              />
            </div>
            
            <div>
              <label style={{ display: 'block', marginBottom: 4, fontSize: 14, fontWeight: '500' }}>
                Username *
              </label>
              <input
                type="text"
                value={currentConfig.username}
                onChange={(e) => setCurrentConfig(prev => ({ ...prev, username: e.target.value }))}
                placeholder="Username"
                style={{
                  width: '100%',
                  padding: '8px 12px',
                  border: '1px solid #ddd',
                  borderRadius: 4,
                  fontSize: 14
                }}
              />
            </div>
            
            <div>
              <label style={{ display: 'block', marginBottom: 4, fontSize: 14, fontWeight: '500' }}>
                Password *
              </label>
              <input
                type="password"
                value={currentConfig.password}
                onChange={(e) => setCurrentConfig(prev => ({ ...prev, password: e.target.value }))}
                placeholder="Password"
                style={{
                  width: '100%',
                  padding: '8px 12px',
                  border: '1px solid #ddd',
                  borderRadius: 4,
                  fontSize: 14
                }}
              />
            </div>
          </>
        )
    }
  }

  return (
    <div style={{ padding: 16 }}>
      {/* Status Card */}
      <div style={{
        background: '#f8f9fa',
        border: '1px solid #dee2e6',
        borderRadius: 8,
        padding: 20,
        marginBottom: 16
      }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            <span style={{ fontSize: 32 }}>{getProviderIcon(selectedProvider)}</span>
            <div>
              <h3 style={{ margin: 0, fontSize: 20 }}>{getProviderName(selectedProvider)} Integration</h3>
              <div style={{ fontSize: 14, color: '#666', marginTop: 4 }}>
                {availableProviders?.find(p => p.id === selectedProvider)?.description}
              </div>
            </div>
          </div>
          
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <button 
              onClick={handleSelectProviderClick}
              className="btn-secondary"
              style={{ padding: '8px 16px', fontSize: 14 }}
            >
              Change Provider
            </button>
            <button 
              onClick={handleConfigureClick}
              className="btn-primary"
              style={{ padding: '8px 16px', fontSize: 14 }}
            >
              {syncStatus.status === 'connected' ? 'Reconfigure' : 'Configure'}
            </button>
          </div>
        </div>
        
        {/* Status Indicators */}
        <div style={{ 
          display: 'grid', 
          gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', 
          gap: 16,
          marginBottom: 16 
        }}>
          <div style={{
            padding: 12,
            background: 'white',
            border: '1px solid #e0e0e0',
            borderRadius: 4,
            textAlign: 'center'
          }}>
            <div style={{ fontSize: 12, color: '#666', marginBottom: 4 }}>Connection Status</div>
            <div style={{ 
              fontSize: 14, 
              fontWeight: 'bold',
              color: syncStatus.status === 'connected' ? '#4caf50' : '#f44336'
            }}>
              {syncStatus.status === 'connected' ? '✅ Connected' : '❌ Disconnected'}
            </div>
          </div>
          
          <div style={{
            padding: 12,
            background: 'white',
            border: '1px solid #e0e0e0',
            borderRadius: 4,
            textAlign: 'center'
          }}>
            <div style={{ fontSize: 12, color: '#666', marginBottom: 4 }}>Last Sync</div>
            <div style={{ fontSize: 14, fontWeight: 'bold' }}>
              {syncStatus.lastSync ? new Date(syncStatus.lastSync).toLocaleString() : 'Never'}
            </div>
          </div>
          
          <div style={{
            padding: 12,
            background: 'white',
            border: '1px solid #e0e0e0',
            borderRadius: 4,
            textAlign: 'center'
          }}>
            <div style={{ fontSize: 12, color: '#666', marginBottom: 4 }}>Pending Mappings</div>
            <div style={{ fontSize: 14, fontWeight: 'bold' }}>
              {syncStatus.pendingMappings || 0}
            </div>
          </div>
          
          <div style={{
            padding: 12,
            background: 'white',
            border: '1px solid #e0e0e0',
            borderRadius: 4,
            textAlign: 'center'
          }}>
            <div style={{ fontSize: 12, color: '#666', marginBottom: 4 }}>Sync Frequency</div>
            <div style={{ fontSize: 14, fontWeight: 'bold', textTransform: 'capitalize' }}>
              {currentConfig.syncFrequency || 'Manual'}
            </div>
          </div>
        </div>
        
        {/* Connection Actions */}
        {syncStatus.status !== 'connected' ? (
          <div style={{
            padding: 16,
            background: '#fff3e0',
            border: '1px solid #ffb74d',
            borderRadius: 4,
            textAlign: 'center'
          }}>
            <div style={{ fontSize: 16, marginBottom: 12, color: '#e65100' }}>
              ⚠️ {getProviderName(selectedProvider)} is not connected
            </div>
            <div style={{ marginBottom: 16 }}>
              Click "Configure" to set up the integration and start syncing data.
            </div>
            <button 
              onClick={handleConfigureClick}
              className="btn-primary"
              style={{ padding: '10px 24px', fontSize: 16 }}
            >
              Start Configuration
            </button>
          </div>
        ) : (
          // Sync Options Quick Panel (moved here from conditional rendering)
          <div style={{
            padding: '12px 16px',
            background: '#e3f2fd',
            border: '1px solid #2196f3',
            borderRadius: 4,
            fontSize: 14
          }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
              <strong>Quick Sync Options:</strong>
              <div style={{ fontSize: 12, color: '#666' }}>
                Last sync: {syncStatus.lastSync ? new Date(syncStatus.lastSync).toLocaleString() : 'Never'}
              </div>
            </div>
            
            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 8 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 4, fontSize: 12 }}>
                <div style={{
                  width: 8,
                  height: 8,
                  borderRadius: '50%',
                  background: currentConfig.syncOptions?.invoices ? '#4caf50' : '#ddd'
                }}></div>
                <span>Invoices</span>
                <span style={{ color: '#666' }}>({syncStatus.syncSummary?.invoices || 0})</span>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 4, fontSize: 12 }}>
                <div style={{
                  width: 8,
                  height: 8,
                  borderRadius: '50%',
                  background: currentConfig.syncOptions?.payments ? '#4caf50' : '#ddd'
                }}></div>
                <span>Payments</span>
                <span style={{ color: '#666' }}>({syncStatus.syncSummary?.payments || 0})</span>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 4, fontSize: 12 }}>
                <div style={{
                  width: 8,
                  height: 8,
                  borderRadius: '50%',
                  background: currentConfig.syncOptions?.customers ? '#4caf50' : '#ddd'
                }}></div>
                <span>Customers</span>
                <span style={{ color: '#666' }}>({syncStatus.syncSummary?.customers || 0})</span>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 4, fontSize: 12 }}>
                <div style={{
                  width: 8,
                  height: 8,
                  borderRadius: '50%',
                  background: currentConfig.syncOptions?.products ? '#4caf50' : '#ddd'
                }}></div>
                <span>Products</span>
                <span style={{ color: '#666' }}>({syncStatus.syncSummary?.products || 0})</span>
              </div>
            </div>
            
            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
              <button 
                onClick={() => handleSync('full')}
                className="btn-primary"
                style={{ fontSize: 12, padding: '4px 12px' }}
                disabled={isSyncing}
              >
                {isSyncing ? 'Syncing...' : 'Full Sync'}
              </button>
              <button 
                onClick={() => handleSync('incremental')}
                className="btn-secondary"
                style={{ fontSize: 12, padding: '4px 12px' }}
                disabled={isSyncing}
              >
                Incremental Sync
              </button>
              <button 
                onClick={handleSelectiveSync}
                className="btn-secondary"
                style={{ fontSize: 12, padding: '4px 12px' }}
                disabled={isSyncing}
              >
                Selective Sync
              </button>
              <button 
                onClick={handleConfigureClick}
                className="btn-secondary"
                style={{ fontSize: 12, padding: '4px 12px' }}
              >
                Edit Options
              </button>
            </div>
          </div>
        )}
      </div>
      
      {/* Sync Results Display */}
      {syncResult && (
        <div style={{
          padding: 12,
          background: '#e3f2fd',
          border: '1px solid #2196f3',
          borderRadius: 4,
          marginBottom: 16,
          fontSize: 14
        }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <div>
              <strong>Last Sync Results:</strong>
              <span style={{ marginLeft: 12 }}>
                ✅ Created: {syncResult.created} | 
                🔄 Updated: {syncResult.updated} | 
                ⏭️ Skipped: {syncResult.skipped}
              </span>
            </div>
            <button 
              onClick={() => setSyncResult(null)}
              style={{ background: 'none', border: 'none', fontSize: 20, cursor: 'pointer' }}
            >
              ×
            </button>
          </div>
          {syncResult.errors?.length > 0 && (
            <div style={{ marginTop: 8, fontSize: 12 }}>
              <strong>Errors:</strong>
              <ul style={{ margin: 4, paddingLeft: 20 }}>
                {syncResult.errors.slice(0, 3).map((error, index) => (
                  <li key={index}>{error.externalId}: {error.error}</li>
                ))}
                {syncResult.errors.length > 3 && (
                  <li>...and {syncResult.errors.length - 3} more</li>
                )}
              </ul>
            </div>
          )}
        </div>
      )}
      
    
      {/* Configuration Modal */}
   
  <div style={{ padding: 16 }}>
    {/* ... all your existing JSX ... */}
    
    {/* Sync Results Display */}
    {syncResult && (
      <div style={{
        padding: 12,
        background: '#e3f2fd',
        border: '1px solid #2196f3',
        borderRadius: 4,
        marginBottom: 16,
        fontSize: 14
      }}>
        {/* ... sync results content ... */}
      </div>
    )}
    
    {/* Provider Selection Modal - ADD THIS */}
    {showProviderSelectModal && (
      <div className="modal-overlay" style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        background: 'rgba(0,0,0,0.5)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 1000
      }}>
        <div className="modal" style={{
          background: 'white',
          padding: 24,
          borderRadius: 8,
          width: 600,
          maxWidth: '90vw',
          maxHeight: '90vh',
          overflow: 'auto'
        }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
            <h3 style={{ margin: 0 }}>Select Integration Provider</h3>
            <button 
              onClick={() => setShowProviderSelectModal(false)}
              style={{ background: 'none', border: 'none', fontSize: 20, cursor: 'pointer' }}
            >
              ×
            </button>
          </div>
          
          <div style={{ 
            display: 'grid', 
            gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))', 
            gap: 12 
          }}>
            {availableProviders.map(provider => (
              <div 
                key={provider.id}
                onClick={() => handleSelectProvider(provider.id)}
                style={{
                  padding: 16,
                  border: `2px solid ${selectedProvider === provider.id ? '#4caf50' : '#e0e0e0'}`,
                  borderRadius: 8,
                  cursor: 'pointer',
                  background: selectedProvider === provider.id ? '#f1f8e9' : 'white',
                  transition: 'all 0.2s'
                }}
              >
                <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 8 }}>
                  <span style={{ fontSize: 24 }}>{provider.icon}</span>
                  <div>
                    <div style={{ fontWeight: 'bold', fontSize: 16 }}>{provider.name}</div>
                    <div style={{ fontSize: 12, color: '#666' }}>{provider.description}</div>
                  </div>
                </div>
                
                {/* Show status if configured */}
                {providerConfigs[provider.id]?.enabled && (
                  <div style={{
                    marginTop: 8,
                    padding: '4px 8px',
                    background: '#4caf50',
                    color: 'white',
                    borderRadius: 4,
                    fontSize: 12,
                    display: 'inline-block'
                  }}>
                    ✓ Configured
                  </div>
                )}
              </div>
            ))}
          </div>
          
          <div style={{ marginTop: 20, textAlign: 'center' }}>
            <button 
              onClick={() => setShowProviderSelectModal(false)}
              className="btn-secondary"
              style={{ padding: '8px 24px' }}
            >
              Cancel
            </button>
          </div>
        </div>
      </div>
    )}
    
    {/* Configuration Modal - ADD THIS */}
    {showConfigModal && (
      <div className="modal-overlay" style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        background: 'rgba(0,0,0,0.5)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 1001
      }}>
        <div className="modal" style={{
          background: 'white',
          padding: 24,
          borderRadius: 8,
          width: 500,
          maxWidth: '90vw',
          maxHeight: '90vh',
          overflow: 'auto'
        }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
            <div>
              <h3 style={{ margin: 0, display: 'flex', alignItems: 'center', gap: 8 }}>
                <span>{getProviderIcon(selectedProvider)}</span>
                Configure {getProviderName(selectedProvider)}
              </h3>
              <div style={{ fontSize: 12, color: '#666', marginTop: 4 }}>
                {availableProviders?.find(p => p.id === selectedProvider)?.description}
              </div>
            </div>
            <button 
              onClick={() => setShowConfigModal(false)}
              style={{ background: 'none', border: 'none', fontSize: 20, cursor: 'pointer' }}
            >
              ×
            </button>
          </div>
          
          <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
            {/* Test Connection Result */}
            {syncStatus.testResult && (
              <div style={{
                padding: 12,
                background: syncStatus.testResult.success ? '#e8f5e9' : '#ffebee',
                border: `1px solid ${syncStatus.testResult.success ? '#4caf50' : '#f44336'}`,
                borderRadius: 4
              }}>
                <strong>Test Result:</strong> {syncStatus.testResult.message}
                {syncStatus.testResult.version && (
                  <div style={{ marginTop: 4, fontSize: 12 }}>
                    Version: {syncStatus.testResult.version}
                  </div>
                )}
              </div>
            )}
            
            {/* Configuration Form */}
            {renderProviderSpecificFields()}
            
            <div>
              <label style={{ display: 'block', marginBottom: 4, fontSize: 14, fontWeight: '500' }}>
                Sync Frequency
              </label>
              <select
                value={currentConfig.syncFrequency}
                onChange={(e) => setCurrentConfig(prev => ({ ...prev, syncFrequency: e.target.value }))}
                style={{
                  width: '100%',
                  padding: '8px 12px',
                  border: '1px solid #ddd',
                  borderRadius: 4,
                  fontSize: 14,
                  background: 'white'
                }}
              >
                <option value="manual">Manual Only</option>
                <option value="daily">Daily</option>
                <option value="weekly">Weekly</option>
                <option value="realtime">Real-time (Polling)</option>
              </select>
            </div>
            
            {/* Sync Options Section */}
            {renderSyncOptions()}
          </div>
          
          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 12, marginTop: 24 }}>
            <button
              onClick={handleTestConnection}
              className="btn-secondary"
              disabled={isTesting}
              style={{ padding: '8px 16px' }}
            >
              {isTesting ? 'Testing...' : 'Test Connection'}
            </button>
            <button
              onClick={handleSaveConfig}
              className="btn-primary"
              style={{ padding: '8px 16px' }}
            >
              Save & Connect
            </button>
          </div>
          
          <div style={{ marginTop: 20, padding: 12, background: '#fff3e0', borderRadius: 4, fontSize: 12 }}>
            <strong>Note:</strong> Credentials are encrypted and stored securely. Only authorized users can access integration settings.
          </div>
        </div>
      </div>
    )}
    
  </div> 

    </div>
  );
}

export default IntegrationTab;
